import { open } from "@tauri-apps/plugin-shell";
import { useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { useSetImmerAtom, withImmer } from "jotai-immer";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import saveFile from "save-file";
import { uid } from "uid";
import { currentTimeAtom } from "$/modules/audio/states/index.ts";
import { clipboardLinesAtom } from "$/states/clipboard.ts";
import { useFileOpener } from "$/hooks/useFileOpener.ts";
import exportTTMLText from "$/modules/project/logic/ttml-writer";
import { applyRomanizationWarnings } from "$/modules/segmentation/utils/Transliteration/roman-warning";
import { segmentLyricLines } from "$/modules/segmentation/utils/segmentation";
import { useSegmentationConfig } from "$/modules/segmentation/utils/useSegmentationConfig";
import {
	advancedSegmentationDialogAtom,
	confirmDialogAtom,
	distributeRomanizationDialogAtom,
	historyRestoreDialogAtom,
	latencyTestDialogAtom,
	metadataEditorDialogAtom,
	settingsDialogAtom,
	submitToAMLLDBDialogAtom,
	timeShiftDialogAtom,
} from "$/states/dialogs.ts";
import {
	keyCopySelectionAtom,
	keyDeleteSelectionAtom,
	keyNewFileAtom,
	keyOpenFileAtom,
	keyPasteSelectionAtom,
	keyRedoAtom,
	keySaveFileAtom,
	keySelectAllAtom,
	keySelectInvertedAtom,
	keySelectWordsOfMatchedSelectionAtom,
	keyUndoAtom,
} from "$/states/keybindings.ts";
import {
	isDirtyAtom,
	lyricLinesAtom,
	newLyricLinesAtom,
	projectIdAtom,
	redoLyricLinesAtom,
	saveFileNameAtom,
	selectedLinesAtom,
	selectedWordsAtom,
	undoableLyricLinesAtom,
	undoLyricLinesAtom,
} from "$/states/main.ts";
import { newLyricWord } from "$/types/ttml.ts";
import { error, log } from "$/utils/logging.ts";

export const useTopMenuActions = () => {
	const { t } = useTranslation();
	const [saveFileName, setSaveFileName] = useAtom(saveFileNameAtom);
	const newLyricLine = useSetAtom(newLyricLinesAtom);
	const editLyricLines = useSetImmerAtom(lyricLinesAtom);
	const setMetadataEditorOpened = useSetAtom(metadataEditorDialogAtom);
	const setSettingsDialogOpened = useSetAtom(settingsDialogAtom);
	const undoLyricLines = useAtomValue(undoableLyricLinesAtom);
	const store = useStore();
	const isDirty = useAtomValue(isDirtyAtom);
	const setConfirmDialog = useSetAtom(confirmDialogAtom);
	const setHistoryRestoreDialog = useSetAtom(historyRestoreDialogAtom);
	const setAdvancedSegmentationDialog = useSetAtom(
		advancedSegmentationDialogAtom,
	);
	const setTimeShiftDialog = useSetAtom(timeShiftDialogAtom);
	const { openFile } = useFileOpener();
	const setProjectId = useSetAtom(projectIdAtom);
	const setDistributeRomanizationDialog = useSetAtom(
		distributeRomanizationDialogAtom,
	);
	const { config: segmentationConfig } = useSegmentationConfig();
	const newFileKey = useAtomValue(keyNewFileAtom);
	const openFileKey = useAtomValue(keyOpenFileAtom);
	const saveFileKey = useAtomValue(keySaveFileAtom);
	const undoKey = useAtomValue(keyUndoAtom);
	const redoKey = useAtomValue(keyRedoAtom);
	const selectAllLinesKey = useAtomValue(keySelectAllAtom);
	const selectInvertedLinesKey = useAtomValue(keySelectInvertedAtom);
	const selectWordsOfMatchedSelectionKey = useAtomValue(
		keySelectWordsOfMatchedSelectionAtom,
	);
	const deleteSelectionKey = useAtomValue(keyDeleteSelectionAtom);
	const copySelectionKey = useAtomValue(keyCopySelectionAtom);
	const pasteSelectionKey = useAtomValue(keyPasteSelectionAtom);

	const onNewFile = useCallback(() => {
		const action = () => {
			newLyricLine();
			setProjectId(uid());
			setSaveFileName("lyric.ttml");
		};

		if (isDirty) {
			setConfirmDialog({
				open: true,
				title: t("confirmDialog.newFile.title", "确认新建文件"),
				description: t(
					"confirmDialog.newFile.description",
					"当前文件有未保存的更改。如果继续，这些更改将会丢失。确定要新建文件吗？",
				),
				onConfirm: action,
			});
		} else {
			action();
		}
	}, [
		isDirty,
		newLyricLine,
		setConfirmDialog,
		t,
		setProjectId,
		setSaveFileName,
	]);

	const onOpenFile = useCallback(() => {
		const inputEl = document.createElement("input");
		inputEl.type = "file";
		inputEl.accept = ".ttml,.lrc,.qrc,.eslrc,.lys,.yrc,*/*";
		inputEl.addEventListener(
			"change",
			() => {
				const file = inputEl.files?.[0];
				if (!file) return;
				openFile(file);
			},
			{
				once: true,
			},
		);
		inputEl.click();
	}, [openFile]);

	const onOpenFileFromClipboard = useCallback(async () => {
		try {
			const ttmlText = await navigator.clipboard.readText();
			const file = new File([ttmlText], "lyric.ttml", {
				type: "application/xml",
			});
			openFile(file);
		} catch (e) {
			error("Failed to parse TTML file from clipboard", e);
		}
	}, [openFile]);

	const onSaveFile = useCallback(() => {
		try {
			const ttmlText = exportTTMLText(store.get(lyricLinesAtom));
			const b = new Blob([ttmlText], { type: "text/plain" });
			saveFile(b, saveFileName).catch(error);
		} catch (e) {
			error("Failed to save TTML file", e);
		}
	}, [saveFileName, store]);

	const onOpenHistoryRestore = useCallback(() => {
		setHistoryRestoreDialog(true);
	}, [setHistoryRestoreDialog]);

	const onSaveFileToClipboard = useCallback(async () => {
		try {
			const lyric = store.get(lyricLinesAtom);
			const ttml = exportTTMLText(lyric);
			await navigator.clipboard.writeText(ttml);
		} catch (e) {
			error("Failed to save TTML file into clipboard", e);
		}
	}, [store]);

	const onSubmitToAMLLDB = useCallback(() => {
		store.set(submitToAMLLDBDialogAtom, true);
	}, [store]);

	const onOpenMetadataEditor = useCallback(() => {
		setMetadataEditorOpened(true);
	}, [setMetadataEditorOpened]);

	const onOpenSettings = useCallback(() => {
		setSettingsDialogOpened(true);
	}, [setSettingsDialogOpened]);

	const onOpenLatencyTest = useCallback(() => {
		store.set(latencyTestDialogAtom, true);
	}, [store]);

	const onOpenGitHub = useCallback(async () => {
		if (import.meta.env.TAURI_ENV_PLATFORM) {
			await open("https://github.com/Steve-xmh/amll-ttml-tool");
		} else {
			window.open("https://github.com/Steve-xmh/amll-ttml-tool");
		}
	}, []);

	const onOpenWiki = useCallback(async () => {
		if (import.meta.env.TAURI_ENV_PLATFORM) {
			await open("https://github.com/Steve-xmh/amll-ttml-tool/wiki");
		} else {
			window.open("https://github.com/Steve-xmh/amll-ttml-tool/wiki");
		}
	}, []);

	const onUndo = useCallback(() => {
		store.set(undoLyricLinesAtom);
	}, [store]);

	const onRedo = useCallback(() => {
		store.set(redoLyricLinesAtom);
	}, [store]);

	const onUnselectAll = useCallback(() => {
		const immerSelectedLinesAtom = withImmer(selectedLinesAtom);
		const immerSelectedWordsAtom = withImmer(selectedWordsAtom);
		store.set(immerSelectedLinesAtom, (old) => {
			old.clear();
		});
		store.set(immerSelectedWordsAtom, (old) => {
			old.clear();
		});
	}, [store]);

	const onSelectAll = useCallback(() => {
		const lines = store.get(lyricLinesAtom).lyricLines;
		const selectedLineIds = store.get(selectedLinesAtom);
		const selectedLines = lines.filter((l) => selectedLineIds.has(l.id));
		const selectedWordIds = store.get(selectedWordsAtom);
		const selectedWords = lines
			.flatMap((l) => l.words)
			.filter((w) => selectedWordIds.has(w.id));
		if (selectedWords.length > 0) {
			const tmpWordIds = new Set(selectedWordIds);
			for (const selLine of selectedLines) {
				for (const word of selLine.words) {
					tmpWordIds.delete(word.id);
				}
			}
			if (tmpWordIds.size === 0) {
				store.set(
					selectedWordsAtom,
					new Set(selectedLines.flatMap((line) => line.words.map((w) => w.id))),
				);
				return;
			}
		} else {
			store.set(
				selectedLinesAtom,
				new Set(store.get(lyricLinesAtom).lyricLines.map((l) => l.id)),
			);
		}
		const sel = window.getSelection();
		if (sel) {
			if (sel.empty) {
				sel.empty();
			} else if (sel.removeAllRanges) {
				sel.removeAllRanges();
			}
		}
	}, [store]);

	const onSelectInverted = useCallback(() => { }, []);

	const onSelectWordsOfMatchedSelection = useCallback(() => { }, []);

	const onDeleteSelection = useCallback(() => {
		const selectedWordIds = store.get(selectedWordsAtom);
		const selectedLineIds = store.get(selectedLinesAtom);
		log("deleting selections", selectedWordIds, selectedLineIds);
		if (selectedWordIds.size === 0) {
			editLyricLines((prev) => {
				prev.lyricLines = prev.lyricLines.filter(
					(l) => !selectedLineIds.has(l.id),
				);
			});
		} else {
			editLyricLines((prev) => {
				for (const line of prev.lyricLines) {
					line.words = line.words.filter((w) => !selectedWordIds.has(w.id));
				}
			});
		}
		store.set(selectedWordsAtom, new Set());
		store.set(selectedLinesAtom, new Set());
	}, [store, editLyricLines]);

	const onCopySelection = useCallback(() => {
		const selectedLineIds = store.get(selectedLinesAtom);
		const selectedWordIds = store.get(selectedWordsAtom);
		if (selectedLineIds.size === 0 && selectedWordIds.size === 0) return;

		const allLines = store.get(lyricLinesAtom).lyricLines;
		let linesToCopy = allLines.filter((l) => selectedLineIds.has(l.id));

		// If we select specific words, we should only copy those words, 
		// and tighten the line's start/end time around them.
		if (selectedWordIds.size > 0) {
			linesToCopy = linesToCopy.map(l => {
				const filteredWords = l.words.filter(w => selectedWordIds.has(w.id));
				if (filteredWords.length > 0) {
					return {
						...l,
						words: filteredWords,
						startTime: filteredWords[0].startTime,
						endTime: filteredWords[filteredWords.length - 1].endTime
					};
				}
				// If the line has no selected words but the line itself is selected,
				// check if it was explicitly selected (e.g. via marquee hitting the line but no words)
				// In that case keep all words? The previous logic deleted words if selectedWordIds > 0.
				// For copy, if they explicitly only targeted words, we'll exclude empty lines.
				return { ...l, words: [] };
			}).filter(l => l.words.length > 0 || (l.words.length === 0 && l.startTime !== 0 && l.endTime !== 0 && selectedLineIds.has(l.id) && l.id !== "")); // Hacky check to keep empty lines if strictly selected

			// Filter out any lines that lost all words (unless it started with no words and was selected)
			linesToCopy = linesToCopy.filter(l => l.words.length > 0 || (allLines.find(al => al.id === l.id)?.words.length === 0));
		}

		if (linesToCopy.length === 0) return;

		// Deep clone the lines
		const cloned = JSON.parse(JSON.stringify(linesToCopy));
		store.set(clipboardLinesAtom, cloned);
	}, [store]);

	const onPasteSelection = useCallback(() => {
		const clipboardLines = store.get(clipboardLinesAtom);
		if (clipboardLines.length === 0) return;

		// Find the minimum start time across all clipboard lines to compute the offset
		let minStartTime = Infinity;
		for (const line of clipboardLines) {
			if (line.startTime < minStartTime) minStartTime = line.startTime;
		}

		const currentTime = store.get(currentTimeAtom); // in ms
		const offset = currentTime - minStartTime;

		// Deep clone and assign new IDs, apply time offset
		const newLines = clipboardLines.map((line: typeof clipboardLines[0]) => {
			const newLine = {
				...JSON.parse(JSON.stringify(line)),
				id: uid(),
				startTime: line.startTime + offset,
				endTime: line.endTime + offset,
				words: line.words.map((w: typeof line.words[0]) => ({
					...JSON.parse(JSON.stringify(w)),
					id: uid(),
					startTime: w.startTime + offset,
					endTime: w.endTime + offset,
				})),
			};
			return newLine;
		});

		editLyricLines((draft) => {
			draft.lyricLines.push(...newLines);
			// Sort by start time to maintain proper order
			draft.lyricLines.sort((a, b) => a.startTime - b.startTime);
		});

		// Select the newly pasted lines
		store.set(selectedLinesAtom, new Set(newLines.map((l: typeof newLines[0]) => l.id)));
		store.set(selectedWordsAtom, new Set());
	}, [store, editLyricLines]);

	const onAutoSegment = useCallback(() => {
		editLyricLines((draft) => {
			draft.lyricLines = segmentLyricLines(
				draft.lyricLines,
				segmentationConfig,
			);
		});
	}, [editLyricLines, segmentationConfig]);

	const onOpenTimeShift = useCallback(() => {
		setTimeShiftDialog(true);
	}, [setTimeShiftDialog]);

	const onSyncLineTimestamps = useCallback(() => {
		const action = () => {
			editLyricLines((draft) => {
				for (let i = 0; i < draft.lyricLines.length; i++) {
					const line = draft.lyricLines[i];
					if (line.words.length === 0) continue;

					let startTime = line.words[0].startTime;
					let endTime = line.words[line.words.length - 1].endTime;

					if (i + 1 < draft.lyricLines.length) {
						const nextLine = draft.lyricLines[i + 1];
						if (nextLine.isBG && nextLine.words.length > 0) {
							const nextLineStart = nextLine.words[0].startTime;
							const nextLineEnd =
								nextLine.words[nextLine.words.length - 1].endTime;
							startTime = Math.min(startTime, nextLineStart);
							endTime = Math.max(endTime, nextLineEnd);
						}
					}

					line.startTime = startTime;
					line.endTime = endTime;
				}
			});
		};

		setConfirmDialog({
			open: true,
			title: t("confirmDialog.syncLineTimestamps.title", "确认同步行时间戳"),
			description: t(
				"confirmDialog.syncLineTimestamps.description",
				"此操作将根据每行单词的时间戳自动同步所有行的起始和结束时间为第一个和最后一个音节的开始和结束时间。确定要继续吗？",
			),
			onConfirm: action,
		});
	}, [editLyricLines, setConfirmDialog, t]);

	const onOpenDistributeRomanization = useCallback(() => {
		setDistributeRomanizationDialog(true);
	}, [setDistributeRomanizationDialog]);

	const onCheckRomanizationWarnings = useCallback(() => {
		editLyricLines((draft) => {
			for (const line of draft.lyricLines) {
				applyRomanizationWarnings(line.words);
			}
		});
	}, [editLyricLines]);

	const onMergeBackgroundLines = useCallback(() => {
		const action = () => {
			editLyricLines((draft) => {
				let i = 0;
				while (i < draft.lyricLines.length) {
					const mainLine = draft.lyricLines[i];
					if (!mainLine.isBG) {
						let j = i + 1;
						let bgCount = 0;
						while (j < draft.lyricLines.length && draft.lyricLines[j].isBG) {
							const bgLine = draft.lyricLines[j];
							bgCount++;

							const isLastBgLine =
								j + 1 >= draft.lyricLines.length || !draft.lyricLines[j + 1].isBG;

							if (bgLine.words.length > 0) {
								if (bgCount === 1) {
									bgLine.words[0].word = ` (${bgLine.words[0].word}`;
								} else {
									bgLine.words[0].word = `, ${bgLine.words[0].word}`;
								}

								if (isLastBgLine) {
									bgLine.words[bgLine.words.length - 1].word = `${bgLine.words[bgLine.words.length - 1].word
										})`;
								}
								mainLine.words.push(...bgLine.words);
							}

							if (bgLine.endTime > mainLine.endTime)
								mainLine.endTime = bgLine.endTime;

							draft.lyricLines.splice(j, 1);
						}
					}
					i++;
				}
			});
		};

		setConfirmDialog({
			open: true,
			title: t("confirmDialog.mergeBackgroundLines.title", "合并背景歌词"),
			description: t(
				"confirmDialog.mergeBackgroundLines.description",
				"这会将所有背景歌词行合并到它们上方的主歌词行中，并将其包裹在括号内。确定要继续吗？",
			),
			onConfirm: action,
		});
	}, [editLyricLines, setConfirmDialog, t]);

	const onUnmergeBackgroundLines = useCallback(() => {
		const action = () => {
			editLyricLines((draft) => {
				for (let i = 0; i < draft.lyricLines.length; i++) {
					const line = draft.lyricLines[i];
					const newBgLines: typeof draft.lyricLines = [];
					let j = 0;

					while (j < line.words.length) {
						let word = line.words[j].word;

						// Check if current word starts a background group e.g. "(Yeah"
						if (word.trim().startsWith("(")) {
							const bgLineStrartIndex = j;
							let bgLineEndIndex = j;

							// Find the end of the group
							while (bgLineEndIndex < line.words.length) {
								if (line.words[bgLineEndIndex].word.trim().endsWith(")")) {
									break;
								}
								bgLineEndIndex++;
							}

							// If we found a matching end parenthesis
							if (bgLineEndIndex < line.words.length) {
								// Extract the words
								const extractedWords = line.words.splice(bgLineStrartIndex, bgLineEndIndex - bgLineStrartIndex + 1);

								// Process formatting commas and parentheses 
								const newLineToInsert = { ...line, id: uid(), words: [] as typeof line.words, isBG: true };

								// Process and isolate background strings (split by comma if multiple backgrounds)
								let currentBgLine = { ...newLineToInsert, id: uid(), words: [] as typeof line.words };
								for (let k = 0; k < extractedWords.length; k++) {
									const bgWord = extractedWords[k];
									let text = bgWord.word.trim();

									let shouldEndCurrentBg = false;
									if (text.startsWith("(")) text = text.substring(1).trimStart();
									if (text.endsWith(")")) {
										text = text.substring(0, text.length - 1).trimEnd();
										shouldEndCurrentBg = true;
									}
									if (text.startsWith(",")) {
										// This is a new bg line
										newBgLines.push(currentBgLine);
										currentBgLine = { ...newLineToInsert, id: uid(), words: [] as typeof line.words };
										text = text.substring(1).trimStart();
									}

									bgWord.word = text;
									currentBgLine.words.push(bgWord);

									if (shouldEndCurrentBg) {
										newBgLines.push(currentBgLine);
										currentBgLine = { ...newLineToInsert, id: uid(), words: [] as typeof line.words };
									}
								}

								// Recompute line timings
								for (const bgL of newBgLines) {
									if (bgL.words.length > 0) {
										bgL.startTime = bgL.words[0].startTime;
										bgL.endTime = bgL.words[bgL.words.length - 1].endTime;
									}
								}
								j--; // the array shrunk, so stay at this index to recheck
							}
						}
						j++;
					}

					if (line.words.length > 0) {
						line.startTime = line.words[0].startTime;
						line.endTime = line.words[line.words.length - 1].endTime;
					}

					// Insert extracted lines
					if (newBgLines.length > 0) {
						draft.lyricLines.splice(i + 1, 0, ...newBgLines);
						i += newBgLines.length; // skip the newly inserted lines
					}
				}
			});
		};

		setConfirmDialog({
			open: true,
			title: t("confirmDialog.unmergeBackgroundLines.title", "提取背景歌词"),
			description: t(
				"confirmDialog.unmergeBackgroundLines.description",
				"这会将主歌词行中被括号包裹的词组提取出来变为新的背景歌词行并移除括号。确定要继续吗？",
			),
			onConfirm: action,
		});
	}, [editLyricLines, setConfirmDialog, t]);

	const onOpenAdvancedSegmentation = useCallback(() => {
		setAdvancedSegmentationDialog(true);
	}, [setAdvancedSegmentationDialog]);

	const onClearSync = useCallback(() => {
		editLyricLines((draft) => {
			const selectedLineIds = store.get(selectedLinesAtom);
			const linesToClear = selectedLineIds.size > 0
				? draft.lyricLines.filter((l) => selectedLineIds.has(l.id))
				: draft.lyricLines;
			for (const line of linesToClear) {
				line.startTime = 0;
				line.endTime = 0;
				for (const word of line.words) {
					word.startTime = 0;
					word.endTime = 0;
				}
			}
		});
	}, [editLyricLines, store]);

	const onFixEndTimes = useCallback(() => {
		editLyricLines((draft) => {
			const allLines = draft.lyricLines;
			for (let li = 0; li < allLines.length; li++) {
				const line = allLines[li];
				for (let wi = 0; wi < line.words.length; wi++) {
					const word = line.words[wi];
					if (word.endTime === 0 && word.startTime !== 0) {
						// Find next synchronizable word on same line
						const nextWord = line.words
							.slice(wi + 1)
							.find((w) => w.word.trim().length > 0 && w.startTime !== 0);
						if (nextWord) {
							word.endTime = nextWord.startTime;
						} else if (li + 1 < allLines.length) {
							// Find first word of next line
							const nextLine = allLines[li + 1];
							const nextLineWord = nextLine.words
								.find((w) => w.word.trim().length > 0 && w.startTime !== 0);
							if (nextLineWord) {
								word.endTime = nextLineWord.startTime;
							}
						}
					}
				}
			}
		});
	}, [editLyricLines]);

	const onAddSpaceAfterSelectedWords = useCallback(() => {
		editLyricLines((draft) => {
			const selectedWordIds = store.get(selectedWordsAtom);
			const selectedLineIds = store.get(selectedLinesAtom);
			for (const line of draft.lyricLines) {
				const isLineSelected = selectedLineIds.has(line.id);
				const hasSelectedWord = line.words.some((w) => selectedWordIds.has(w.id));
				if (isLineSelected || hasSelectedWord) {
					const newWords = [];
					for (let i = 0; i < line.words.length; i++) {
						const word = line.words[i];
						newWords.push(word);
						if ((isLineSelected || selectedWordIds.has(word.id)) && i !== line.words.length - 1) {
							newWords.push({
								...newLyricWord(),
								startTime: word.endTime,
								endTime: word.endTime,
								word: " ",
							});
						}
					}
					line.words = newWords;
				}
			}
		});
	}, [editLyricLines, store]);

	return {
		newFileKey,
		openFileKey,
		saveFileKey,
		undoKey,
		redoKey,
		selectAllLinesKey,
		unselectAllLinesKey: selectAllLinesKey,
		selectInvertedLinesKey,
		selectWordsOfMatchedSelectionKey,
		deleteSelectionKey,
		copySelectionKey,
		pasteSelectionKey,
		undoDisabled: !undoLyricLines.canUndo,
		redoDisabled: !undoLyricLines.canRedo,
		onNewFile,
		onOpenFile,
		onOpenFileFromClipboard,
		onSaveFile,
		onOpenHistoryRestore,
		onSaveFileToClipboard,
		onSubmitToAMLLDB,
		onUndo,
		onRedo,
		onSelectAll,
		onUnselectAll,
		onSelectInverted,
		onSelectWordsOfMatchedSelection,
		onDeleteSelection,
		onCopySelection,
		onPasteSelection,
		onOpenTimeShift,
		onOpenMetadataEditor,
		onOpenSettings,
		onAutoSegment,
		onOpenAdvancedSegmentation,
		onSyncLineTimestamps,
		onOpenDistributeRomanization,
		onCheckRomanizationWarnings,
		onOpenLatencyTest,
		onOpenGitHub,
		onOpenWiki,
		onClearSync,
		onFixEndTimes,
		onMergeBackgroundLines,
		onUnmergeBackgroundLines,
		onAddSpaceAfterSelectedWords,
	};
};
